/**
 * Team workspace view model (spec #44 §1, §4).
 *
 * Nothing here is a wire shape: every type in this file is something the
 * workspace COMPOSES out of the sibling contracts (timeline, proposals,
 * plan, hand-offs, specialists). The wire shapes live in their own files
 * (`timeline.ts`, `plan.ts`, `handoffs.ts`, `agentMind.ts`,
 * `actionProposals.ts`, `marketingSuite.ts`) and are never restated here.
 */

import type { AgentSettings } from './agentSettings';
import type { HandoffItem } from './handoffs';
import type { ActionProposal } from './actionProposals';
import type { Specialist } from './marketingSuite';

/**
 * The five derived presence states (§4). `measuring` and `training` from
 * the prototype are deliberately absent: no sibling contract carries them
 * and drawing an unrecorded state is forbidden (HANDOFF §11).
 */
export type PresenceState =
  | 'working'
  | 'needs-you'
  | 'quiet'
  | 'off'
  | 'not-set-up';

/** The three fixed channels plus the per-agent ones. */
export type ChannelId = 'board' | 'needs-you' | 'team-now' | string;

export const BOARD_CHANNEL = 'board';
export const NEEDS_YOU_CHANNEL = 'needs-you';
export const TEAM_NOW_CHANNEL = 'team-now';

/** A row in the sidebar: a fixed channel, an agent, or a specialist. */
export interface ChannelDescriptor {
  id: ChannelId;
  kind: 'fixed' | 'agent' | 'specialist';
  label: string;
  /** Agent rows: their own domain. Specialist rows: their OWNER's domain —
   * a specialist is not its own channel, it opens its owner's (§1, §8). */
  domain?: string;
  /** Specialist rows only. */
  specialistKey?: string;
  presence?: PresenceState;
}

/** Where an open question came from — the questions lane is a union. */
export type QuestionSource = 'proposal' | 'plan_approval' | 'handoff';

/**
 * One row of the questions lane, normalised across its three sources so the
 * lane can sort and count without knowing which table produced it. The
 * original record travels along untouched so the card can render its own
 * face (a proposal keeps the shared `ProposalCard`).
 */
export interface WorkspaceQuestion {
  /** `{source}:{id}` — unique across the union. */
  id: string;
  source: QuestionSource;
  /** The agent this question is attributed to; `null` when nothing in the
   * record names one (never guessed — the card says so instead). */
  owner_domain: string | null;
  title: string;
  occurred_at: string;
  proposal?: ActionProposal;
  handoff?: HandoffItem;
}

/** A live-work tile: an agent mid-cycle, or a specialist's last run. */
export interface LiveWorkTile {
  key: string;
  label: string;
  domain: string;
  /** The agent's newest timeline title, or the specialist's run status. */
  detail: string | null;
  kind: 'agent' | 'specialist';
  status?: string | null;
}

/** One turn of the Ask-backed DM composer (§2 — session-held, never saved). */
export interface AskTurn {
  id: string;
  domain: string;
  question: string;
  asked_at: string;
  answer_md?: string;
  resolved_query?: unknown;
  /** Set when Ask refused: `no_matching_metric` renders a deep link to the
   * agent's beliefs, never a prose guess. */
  error_code?: string;
  error_message?: string;
}

/** The seven labelled empty states of §3. */
export type WorkspaceEmptyKind =
  | 'no-plan'
  | 'no-cycle'
  | 'no-questions'
  | 'no-agents-configured'
  | 'ask-not-entitled'
  | 'no-specialists'
  | 'lane-error';

export interface WorkspaceRosters {
  agents: AgentSettings[];
  specialists: Specialist[];
}
